import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Check, MoreVertical } from "lucide-react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from "@/components/ui";
import { Chip } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { PLATFORM_LABELS } from "../constants";
import type { Opportunity } from "../opportunityModel";

/**
 * One opportunity: an invitation addressed to this creator, or an open campaign
 * they fit.
 *
 * **Flex with wrap, not grid.** A grid cannot reflow, and the money column is
 * the first thing it pushes off the edge in a narrow window — which on this
 * screen is the one number the creator opened the page for. Every column here
 * has a real `flex-basis` and the text columns carry `min-width: 0`.
 *
 * The whole row leads to the campaign through an absolutely-positioned link
 * underneath, with the content marked `pointer-events-none` above it. The menu
 * and the accept button sit above both so they stay clickable.
 */
export function OpportunityRow({
  opportunity,
  onAccept,
  onDismiss,
  busy,
}: {
  opportunity: Opportunity;
  onAccept: () => void;
  onDismiss: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const { campaign, brandName, brandLogo, invited, message, payout, deadline } = opportunity;

  return (
    <article
      className={cn(
        "relative flex flex-wrap items-start gap-[18px] rounded-card border border-neutral-200 bg-white px-5 py-[18px]",
        "shadow-card-rest transition-[border-color,box-shadow] hover:border-neutral-300 hover:shadow-list-hover",
      )}
    >
      <Link to={`/campanas/${campaign.id}`} className="absolute inset-0 z-0 rounded-card">
        <span className="sr-only">{campaign.title}</span>
      </Link>

      {/* 1 · Brand mark, 56px square. Squares are brands; circles are people. */}
      <span className="pointer-events-none relative z-[1] flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-avatar-square-lg border border-neutral-200 bg-neutral-50">
        {brandLogo ? (
          <img src={brandLogo} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <Building2 className="size-6 text-neutral-400" strokeWidth={1.8} />
        )}
      </span>

      {/* 2 · Who and what. min-w-0 with a real basis, never minmax(0,1fr). */}
      <div className="pointer-events-none relative z-[1] min-w-0 flex-[1_1_260px]">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-card-title-sm text-neutral-900">{brandName}</p>
          <Badge variant={invited ? "accent" : "neutral"} stamp>
            {invited
              ? t("marketplace.opportunities.invited")
              : t("marketplace.opportunities.openCampaign")}
          </Badge>
        </div>

        <p className="mt-1 text-meta text-neutral-700">{campaign.title}</p>

        {/* The brand's own words about why they wrote. There is no match score
            anywhere in this product, and this is the honest replacement. */}
        <p
          className={cn(
            "mt-2 line-clamp-2 text-fine leading-normal",
            message ? "text-neutral-600" : "italic text-neutral-400",
          )}
        >
          {message || t("marketplace.opportunities.noMessage")}
        </p>

        {campaign.targetNiches.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {campaign.targetNiches.slice(0, 3).map((n) => (
              <Chip key={n} variant="neutral" interactive={false} className="px-2.5 py-1 text-chip">
                {t(`marketplace.niches.${n}`, { defaultValue: n })}
              </Chip>
            ))}
          </div>
        )}
      </div>

      {/* 3 · Terms. */}
      {campaign.deliverablesSpec && campaign.deliverablesSpec.length > 0 && (
        <div className="pointer-events-none relative z-[1] flex-[0_1_auto]">
          <p className="adv-label">{t("marketplace.opportunities.deliverables")}</p>
          <ul className="mt-1 space-y-0.5">
            {campaign.deliverablesSpec.slice(0, 3).map((d, i) => (
              <li key={i} className="text-card-body font-semibold text-neutral-700">
                <span className="adv-mono text-steel-600">×{d.quantity}</span>{" "}
                {t(`marketplace.formats.${d.format}`)} · {PLATFORM_LABELS[d.platform]}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 4 · Money, pushed right. */}
      <div className="pointer-events-none relative z-[1] ml-auto flex-[0_0_auto] text-right">
        <p className="adv-label">{t("marketplace.opportunities.youEarn")}</p>
        <p
          className={cn(
            "mt-1 whitespace-nowrap",
            payout ? "text-figure-md text-neutral-900" : "text-fine text-neutral-400",
          )}
        >
          {payout ?? t("marketplace.opportunities.rateOpen")}
        </p>
        {deadline && (
          <p
            className={cn(
              "mt-1 whitespace-nowrap text-micro",
              deadline.urgent ? "font-semibold text-warning-fg" : "text-neutral-400",
            )}
          >
            {deadline.label}
          </p>
        )}
      </div>

      {/* 5 · Actions, above the row link. */}
      <div className="relative z-[2] flex flex-[0_0_auto] items-center gap-2 self-center">
        <Tooltip label={t("marketplace.opportunities.acceptTip")} align="end">
          <Button size="sm" disabled={busy} onClick={onAccept}>
            <Check />
            {t("marketplace.opportunities.accept")}
          </Button>
        </Tooltip>

        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("marketplace.opportunities.menu")}
              className="inline-flex size-[34px] items-center justify-center rounded-control text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
            >
              <MoreVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-54">
            <DropdownMenuItem asChild>
              <Link to={`/campanas/${campaign.id}`}>
                {t("marketplace.opportunities.viewCampaign")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {/* Muted until hover, then red. Dismiss is local — see the list. */}
            <DropdownMenuItem
              onSelect={onDismiss}
              className="text-neutral-400 focus:bg-danger-bg focus:text-danger-fg"
            >
              {t("marketplace.opportunities.dismiss")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </article>
  );
}
